import React from 'react'
import ReactSelect, { Props as ReactSelectProps } from 'react-select'
import CreatableSelect from 'react-select/creatable'
import { useTheme } from '@chakra-ui/react'
import {
  selectTheme,
  selectStyles,
  sortOptions as optionsSorter,
  SelectOption,
} from 'src/ui/select-utils'

interface SelectProps {
  id?: string
  name?: string
  value?: string | number | string[] | number[] | SelectOption | SelectOption[] | null
  options: SelectOption[]
  placeholder?: string
  hasError?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  isOptionDisabled?: ReactSelectProps['isOptionDisabled']
  isClearable?: boolean
  isMulti?: boolean
  menuIsOpen?: boolean
  isSearchable?: boolean
  sortOptions?: boolean
  width?: number | string
  onBlur?: any
  onChange: any
  onInputChange?: ReactSelectProps['onInputChange']
  filterOption?: ReactSelectProps['filterOption']
  components?: ReactSelectProps['components']
  styles?: any
  menuPortalTarget?: any
  menuPosition?: any
  menuPlacement?: any
  theme?: any
  isCreatable?: boolean
  formatCreateLabel?: any
}

const Select: React.FC<SelectProps> = ({
  id,
  name,
  value,
  options,
  width = 'auto',
  placeholder = 'Select...',
  hasError = false,
  isDisabled = false,
  isLoading = false,
  isClearable = true,
  isMulti = false,
  isSearchable = true,
  sortOptions = true,
  isOptionDisabled,
  onChange,
  onBlur,
  styles,
  menuPortalTarget,
  menuPosition,
  menuPlacement,
  isCreatable = false,
  formatCreateLabel,
  ...rest
}) => {
  let chakraTheme = useTheme()
  let selectedValue = null

  if (value === null || typeof value === 'undefined') {
    selectedValue = isMulti ? [] : null
  } else if (Array.isArray(value)) {
    const values = value as Array<string | number | SelectOption>
    selectedValue = values.every((item) => typeof item === 'object')
      ? values
      : options.filter((opt) => (values as (string | number)[]).includes(opt.value))
  } else if (typeof value === 'object') {
    selectedValue = value
  } else {
    selectedValue = options.find((opt) => opt.value === value)
  }

  const commonProps = {
    inputId: id,
    name,
    value: selectedValue,
    options: sortOptions ? optionsSorter(options) : options,
    placeholder,
    isDisabled,
    isLoading,
    isClearable,
    isMulti,
    isSearchable,
    isOptionDisabled,
    theme: (theme) => selectTheme({ theme, chakraTheme }),
    styles: {
      ...styles,
      ...selectStyles({ styles, chakraTheme, width, hasError }),
    },
    onChange,
    onBlur,
    menuPortalTarget,
    menuPosition,
    menuPlacement,
  }

  if (isCreatable) {
    return <CreatableSelect {...commonProps} formatCreateLabel={formatCreateLabel} {...rest} />
  }

  return <ReactSelect {...commonProps} menuPlacement={menuPlacement} {...rest} />
}

export default Select
